import { User, UserManager, UserManagerSettings } from "oidc-client";
import { expirationEventName } from "../containers/layout/AppBackdropContainer";
import { Module } from "../models/common/modules";
import IUserDataProps from "../models/identity/user-data";

const compareNumericConvertableArrays = (a: Array<string | number>, b: Array<string | number>): number => {
	for (let index = 0; index < Math.min(a.length, b.length); index++) {
		const result = +a[index] - +b[index];
		if (result !== 0) {
			return result;
		}
	}

	if (a.length === b.length) {
		return 0;
	}

	return a.length > b.length ? 1 : -1;
};

const moduleComparer = (a: Module, b: Module) =>
	compareNumericConvertableArrays(a.code.split(".").filter(Boolean), b.code.split(".").filter(Boolean));

export class AuthenticationService {
	private readonly settings: UserManagerSettings;
	private readonly userManager: UserManager;

	constructor(identityServerUrl: string) {
		const origin = window.location.origin;
		this.settings = {
			authority: identityServerUrl,
			client_id: "platform-spa",
			redirect_uri: `${origin}/signin-callback.html`,
			post_logout_redirect_uri: `${origin}/`,
			response_type: "code",
			scope: "openid email profile identity platform-proxy",
			silent_redirect_uri: `${origin}/silent-renew-callback.html`,
			revokeAccessTokenOnSignout: true,
			automaticSilentRenew: true,
			accessTokenExpiringNotificationTime: 10,
			silentRequestTimeout: 10000,
			loadUserInfo: false,
		};
		this.userManager = new UserManager(this.settings);
		const expirationEvent = new Event(expirationEventName);
		this.userManager.events.addSilentRenewError(() => window.dispatchEvent(expirationEvent));
	}

	getUser(): Promise<User | null> {
		return this.userManager.getUser();
	}

	async getUserProfile(): Promise<IUserDataProps> {
		const user = await this.signIn();
		const apiKey = user?.access_token;

		const response = await fetch("/api/users/current", {
			headers: {
				Authorization: `Bearer ${apiKey}`,
			},
		});
		const userProfile: IUserDataProps = await response.json();

		return {
			...userProfile,
			isSubscriptionActive: !!userProfile.organization?.publicAgreementsEnabled,
			modules: userProfile.modules.sort(moduleComparer).map((m) => ({
				...m,
				childModules: m.childModules.sort(moduleComparer),
			})),
		};
	}

	signIn(): Promise<User> {
		return this.userManager.signinSilent({}).catch((e) => {
			console.info("Silent signin impossible, auth code flow will be used");
			this.signInRedirect();
			throw e;
		});
	}

	endSignIn(): void {
		this.userManager.signinRedirectCallback().catch((e) => console.error(`[ERROR] endSigninMainWindow: ${e.message}`));
	}

	startSignOut(args?: { post_logout_redirect_uri?: string }): void {
		this.userManager.signoutRedirect(args).catch((e) => console.error(`[ERROR] startSignoutMainWindow: ${e.message}`));
		this.userManager
			.revokeAccessToken()
			.catch((e) => console.error(`[ERROR] startSignoutMainWindow - revoke access token: ${e.message}`));
	}

	endSignOut(): void {
		this.userManager
			.signoutRedirectCallback()
			.then((resp) => console.info("signed out", resp))
			.catch((e) => console.error(`[ERROR] endSignoutMainWindow: ${e.message}`));
	}

	private signInRedirect(): void {
		const httpParams = new URLSearchParams(window.location.search || "");
		this.userManager
			.signinRedirect({
				extraQueryParams: Array.from(httpParams.keys()).reduce((a, c) => {
					a[c] = httpParams.get(c);
					return a;
				}, {} as any),
				state: window.location.href,
			})
			.catch((e) => console.error(`[ERROR] signinRedirect: ${e.message}`));
	}
}
